import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

export const fetchContrats = createAsyncThunk("contrats/fetchAll", async ({ page = 1, pageSize = 10 } = {}, { rejectWithValue }) => {
  try { const r = await api.get('/Contrat', { params: { page, pageSize } }); return r.data; } // PagedResult
  catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
});

export const createContrat = createAsyncThunk("contrats/create", async (data, { rejectWithValue }) => {
  try {
    await api.post('/Contrat', data);
    const list = await api.get('/Contrat', { params: { page: 1, pageSize: 10 } });
    return list.data; // PagedResult
  } catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
});

export const updateContrat = createAsyncThunk("contrats/update", async ({ id, data }, { rejectWithValue }) => {
  try { await api.patch(`/Contrat/${id}`, data); return { id, ...data }; }
  catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
});

export const deleteContrat = createAsyncThunk("contrats/delete", async (id, { rejectWithValue }) => {
  try { await api.delete(`/Contrat/${id}`); return id; }
  catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
});

export const fetchContratById = createAsyncThunk("contrats/fetchById", async (id, { rejectWithValue }) => {
  try { const r = await api.get(`/Contrat/${id}`); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
});

export const silentFetchContrats = createAsyncThunk("contrats/silentFetch", async ({ page = 1, pageSize = 10 } = {}, { rejectWithValue }) => {
  try { const r = await api.get('/Contrat', { params: { page, pageSize } }); return r.data; } // PagedResult
  catch (e) { return rejectWithValue(e.response?.data?.message || e.message); }
});

export const downloadContratPdf = createAsyncThunk("contrats/downloadPdf", async (id, { rejectWithValue }) => {
  try {
    const response = await api.get(`/Contrat/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `contrat-${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return id;
  } catch (e) { return rejectWithValue(e.message); }
});

const slice = createSlice({
  name: "contrats",
  initialState: {
    items: [],
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,
    loading: false,
    error: null,
    success: false,
  },
  reducers: {
    resetSuccess: (state) => { state.success = false; },
    clearError: (state) => { state.error = null; },
    markContratSigne: (state, action) => {
      const idx = state.items.findIndex(c => c.id === action.payload);
      if (idx !== -1) state.items[idx].estSigne = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContrats.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchContrats.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.page;
      })
      .addCase(fetchContrats.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    builder
      .addCase(createContrat.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createContrat.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = 1;
        state.success = true;
      })
      .addCase(createContrat.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    builder
      .addCase(deleteContrat.fulfilled, (state, action) => {
        state.items = state.items.filter(i => i.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(deleteContrat.rejected, (state, action) => { state.error = action.payload; });
    builder
      .addCase(fetchContratById.fulfilled, (state, action) => {
        const idx = state.items.findIndex(c => c.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      });
    builder
      .addCase(silentFetchContrats.fulfilled, (state, action) => {
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.page;
      });
    builder
      .addCase(downloadContratPdf.rejected, (state, action) => { state.error = action.payload; });
  },
});

export const { resetSuccess, clearError, markContratSigne } = slice.actions;
export default slice.reducer;
